// src/components/Investment/InvestmentLevelModal.jsx - FIXED CURRENCY CONVERSION
import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './InvestmentLevelModal.css';

const InvestmentLevelModal = ({ 
  isOpen, 
  onClose, 
  currentLevel, 
  currentBalance, 
  requiredLevel = 2,
  requiredBalance = 300000, // Always in NGN
  formatCurrency 
}) => {
  const { currency } = useCurrency();
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchExchangeRate();
    }
  }, [isOpen]);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const convertNGNtoUSD = (amountNGN) => {
    if (!exchangeRate || !exchangeRate.ngn_to_usd) {
      return amountNGN / 1600; // Fallback rate
    }
    return amountNGN * parseFloat(exchangeRate.ngn_to_usd);
  };

  const formatAmount = (amountNGN) => {
    if (currency === 'USD') {
      const usdAmount = convertNGNtoUSD(amountNGN);
      return `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₦${amountNGN.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (!isOpen) return null;

  // Convert required balance for display (needed amount calculation)
  const displayRequiredBalance = currency === 'USD' 
    ? convertNGNtoUSD(requiredBalance) 
    : requiredBalance;

  // Current balance is already in correct format from formatCurrency
  const displayCurrentBalance = currency === 'USD'
    ? convertNGNtoUSD(currentBalance)
    : currentBalance;

  const balanceNeeded = displayRequiredBalance - displayCurrentBalance;
  const levelsToGo = requiredLevel - currentLevel;

  const getLevelColor = (level) => {
    const colors = {
      1: '#94a3b8',
      2: '#cd7f32',
      3: '#a78bfa',
      4: '#f59e0b',
      5: '#10b981'
    };
    return colors[level] || '#666';
  };

  return (
    <div className="investment-level-modal-overlay" onClick={onClose}>
      <div className="investment-level-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          ×
        </button>

        <div className="modal-header">
          <div className="lock-icon">🔒</div>
          <h2>Investment Access Locked</h2>
          <p>Upgrade your account to unlock investment opportunities</p>
        </div>

        <div className="level-requirement-card">
          <div className="requirement-row">
            <div className="requirement-item current">
              <span className="requirement-label">Your Level</span>
              <div 
                className="level-badge"
                style={{ backgroundColor: getLevelColor(currentLevel) }}
              >
                Level {currentLevel}
              </div>
              <span className="balance-text">{formatCurrency(currentBalance)}</span>
            </div>

            <div className="requirement-arrow">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M15 10L25 20L15 30" stroke="#cbd5e1" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>

            <div className="requirement-item required">
              <span className="requirement-label">Required Level</span>
              <div 
                className="level-badge"
                style={{ backgroundColor: getLevelColor(requiredLevel) }}
              >
                Level {requiredLevel}
              </div>
              <span className="balance-text">{formatAmount(requiredBalance)}+</span>
            </div>
          </div>

          <div className="progress-section">
            <div className="progress-info">
              <span>Progress to Level {requiredLevel}</span>
              <span className="progress-percentage">
                {Math.min(100, ((displayCurrentBalance / displayRequiredBalance) * 100)).toFixed(1)}%
              </span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${Math.min(100, (displayCurrentBalance / displayRequiredBalance) * 100)}%`,
                  backgroundColor: getLevelColor(requiredLevel)
                }}
              />
            </div>
            <div className="balance-needed">
              <span>You need</span>
              <strong>
                {currency === 'USD' 
                  ? `${balanceNeeded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : `₦${balanceNeeded.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                }
              </strong>
              <span>more to unlock investments</span>
            </div>
          </div>
        </div>

        <div className="benefits-section">
          <h3>🎯 Investment Benefits at Level {requiredLevel}</h3>
          <ul className="benefits-list">
            <li>
              <span className="benefit-icon">✓</span>
              <span>Access to Private Equity & Pre-IPO stocks</span>
            </li>
            <li>
              <span className="benefit-icon">✓</span>
              <span>Customized investment plans with flexible withdrawals</span>
            </li>
            <li>
              <span className="benefit-icon">✓</span>
              <span>Lock-in periods with potential high returns</span>
            </li>
            <li>
              <span className="benefit-icon">✓</span>
              <span>Portfolio diversification opportunities</span>
            </li>
          </ul>
        </div>

        <div className="modal-actions">
          <button className="action-btn deposit-btn" onClick={() => {
            onClose();
            window.location.href = '/wallet/deposit';
          }}>
            💰 Deposit Funds
          </button>
          <button className="action-btn learn-btn" onClick={() => {
            onClose();
            window.location.href = '/wallet/levels';
          }}>
            📊 Learn About Levels
          </button>
        </div>

        <div className="modal-footer">
          <p>Need help? <a href="/support">Contact Support</a></p>
        </div>
      </div>
    </div>
  );
};

export default InvestmentLevelModal;